import { useRef, type MouseEvent } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatQuantidade } from '@/lib/utils/formatters';
import { LoadingDots } from './LoadingDots';
import {
  estadoDaContagemEmalote,
  type ConsultaDaContagemEmalote,
  type EstadoDaContagemEmalote,
} from './contagem-emalote';

const COR_DO_SENTIDO = {
  entrada: 'border-accent/40 text-accent',
  saida: 'border-red-500/40 text-red-600',
} as const;

interface SeloEmaloteProps {
  contagem: ConsultaDaContagemEmalote;
  /** O mês que a contagem pede, em `yyyy-MM-dd`. */
  periodo: { DataInicio: string; DataFim: string };
  sentido: keyof typeof COR_DO_SENTIDO;
}

function rotuloDoSelo(estado: EstadoDaContagemEmalote): string {
  switch (estado.tipo) {
    case 'erro':
      return 'E-Malote: erro ao carregar a contagem. Clique para tentar de novo.';
    case 'sem-contagem':
      return 'E-Malote: sem contagem.';
    case 'sem-conexao':
      return 'E-Malote: sem conexão com a internet.';
    case 'pronta':
      return `E-Malote: ${formatQuantidade(estado.quantidade)} no período.`;
    default:
      return 'E-Malote: carregando a contagem.';
  }
}

function textoDoSelo(estado: EstadoDaContagemEmalote): string {
  switch (estado.tipo) {
    case 'erro':
      return 'E-Malote: erro';
    case 'sem-contagem':
      return 'E-Malote: sem contagem';
    case 'sem-conexao':
      return 'E-Malote: sem conexão';
    default:
      return 'E-Malote';
  }
}

function TotalDaContagem({ estado }: { estado: EstadoDaContagemEmalote }) {
  switch (estado.tipo) {
    case 'erro':
      return (
        <>
          <div className="font-medium">Não foi possível carregar a contagem do E-Malote.</div>
          {estado.motivo ? <div>Motivo: {estado.motivo}</div> : null}
          <div className="opacity-90">Clique no selo para tentar de novo.</div>
        </>
      );
    case 'sem-contagem':
      return (
        <>
          <div className="font-medium">O E-Malote não devolveu a contagem.</div>
          {estado.motivo ? <div>Motivo: {estado.motivo}</div> : null}
        </>
      );
    case 'sem-conexao':
      return <div>Sem conexão com a internet. A contagem sai quando a conexão voltar.</div>;
    case 'pronta':
      return <div>Total: {formatQuantidade(estado.quantidade)}</div>;
    default:
      return (
        <div>
          Total:{' '}
          <span role="status" aria-label="Carregando a contagem do E-Malote">
            <LoadingDots />
          </span>
        </div>
      );
  }
}

/**
 * O selo é o aviso de erro da contagem: ele fica na tela enquanto o erro durar, um por modelo,
 * no lugar dos toasts que se repetiam a cada busca (LC-11).
 */
export function SeloEmalote({ contagem, periodo, sentido }: SeloEmaloteProps) {
  const estado = estadoDaContagemEmalote(contagem);
  const comAviso =
    estado.tipo === 'erro' || estado.tipo === 'sem-contagem' || estado.tipo === 'sem-conexao';

  /*
    O selo mora dentro do cabeçalho que abre e fecha o modelo. Com erro, o clique é "tentar de
    novo" e para aqui; nos outros estados ele segue até o cabeçalho, como antes. A resposta sem
    contagem também segue: repetir a chamada dá a mesma resposta.

    A tentativa dura até a consulta terminar, com a repetição que o app faz depois de 1 s, e
    nesse meio-tempo o selo mostra os pontos. O segundo clique (o duplo clique, ou quem acha que
    nada aconteceu) também para aqui. A marca é local porque o estado não serve: sem número
    anterior, o React Query zera o erro ao refazer a consulta. E ela se apaga quando a promessa do
    `refetch` termina, e não quando a tela vê o fim da busca: a resposta rápida pode nem passar
    pela tela com os pontos.
  */
  const tentandoDeNovo = useRef(false);
  const aoClicar = (evento: MouseEvent<HTMLButtonElement>) => {
    if (estado.tipo === 'erro') {
      evento.stopPropagation();
      tentandoDeNovo.current = true;
      void contagem.refetch().finally(() => {
        tentandoDeNovo.current = false;
      });
      return;
    }
    if (tentandoDeNovo.current) evento.stopPropagation();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2"
          aria-label={rotuloDoSelo(estado)}
          onClick={aoClicar}
        >
          <span className="text-xs text-muted-foreground">•</span>
          <Badge
            variant="outline"
            className={cn(
              'h-5 px-2 transition-transform duration-150 hover:scale-[1.04]',
              comAviso ? 'border-amber-500/60 text-amber-700' : COR_DO_SENTIDO[sentido]
            )}
          >
            {comAviso ? <AlertTriangle aria-hidden="true" /> : null}
            {textoDoSelo(estado)}
          </Badge>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <div className="flex flex-col gap-1">
          <div className="font-medium">E-Malote</div>
          <div className="opacity-90">
            Período do mês: {periodo.DataInicio} a {periodo.DataFim}
          </div>
          <TotalDaContagem estado={estado} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
